import { Alert, Box, TextField, Typography } from '@mui/material';
import {
  NO_PASTE_INPUT_PROPS,
  getDispensableBudMinIso,
  isLotConfirmed,
} from 'modules/StockManagement/utils/stockLineValidation';
import PropTypes from 'prop-types';
import SYSTEM_DEFICIT from 'common/constants/inventoryLabels';

/** The "record lot by hand" section of AddDrugByLotDialog — Fill Product's manual-lot panel
 *  (POM-6083) transplanted to the scan station. Shown once the typed lot is not in stock under
 *  the selected NDC (the NDC field itself lives in the dialog, above the lot field). Purely
 *  presentational; every rule lives in useManualLotEntry. */
const ManualLotPanel = ({ typedLot, entry }) => {
  const fieldsDisabled = entry.recordableLoading || !entry.hasNdcChoices;
  const confirmMismatch =
    !!entry.confirmLotNumber.trim() && !isLotConfirmed(typedLot, entry.confirmLotNumber);

  let budHelperText = 'Required — the BUD printed on the vial, tomorrow or later';
  if (entry.budTooEarly) budHelperText = entry.budTooEarly;
  else if (entry.budLocked) budHelperText = 'From the existing lot';
  else if (entry.existingLot) {
    budHelperText = 'Required — this lot has no BUD on record; enter it from the label';
  }

  let statusAlert = null;
  if (entry.error && !entry.recordableLoading) {
    statusAlert = (
      <Alert severity='warning' sx={{ py: 0.25 }}>
        {entry.error}
      </Alert>
    );
  } else if (!entry.error && !entry.recordableLoading) {
    statusAlert = (
      <Alert severity='info' sx={{ py: 0.25 }}>
        {entry.lookupPending
          ? 'Checking this lot number against inventory…'
          : `The quantity dispensed will be recorded as a ${SYSTEM_DEFICIT.toLowerCase()} claim on this lot — inventory has no receipt for it yet.`}
      </Alert>
    );
  }

  return (
    <Box sx={{ mt: 2, p: 1.5, borderRadius: 1, border: '1px solid', borderColor: 'warning.light' }}>
      <Typography variant='subtitle2' sx={{ mb: 1 }}>
        Record lot by hand ({SYSTEM_DEFICIT.toLowerCase()})
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 1.5 }}>
        {/* Re-keyed by hand, and pasteable by nothing: a lot number typed from a vial is the one
            value here that no later screen can correct. Absent for a lot that resolved to an
            existing row — that value came from inventory's own row. */}
        {entry.needsLotConfirmation && (
          <TextField
            size='small'
            label='Re-enter lot number'
            required
            value={entry.confirmLotNumber}
            onChange={(e) => entry.setConfirmLotNumber(e.target.value)}
            disabled={fieldsDisabled}
            inputProps={{ ...NO_PASTE_INPUT_PROPS, 'data-testid': 'manual-lot-confirm' }}
            error={confirmMismatch}
            helperText={
              confirmMismatch ? 'Lot numbers do not match' : 'Type it again — paste is disabled'
            }
            sx={{ flex: '1 1 220px' }}
          />
        )}

        {/* Required: inventory identifies a lot by number + BUD, and the operator holding the
            vial is the one person who can read it. Floored at tomorrow — this lot is being
            dispensed, and a date already reached describes stock that cannot go out. Locked once
            the lot resolves to an existing dated row: that BUD is part of its identity, and
            editing it here would describe a different lot than the one claimed. */}
        <TextField
          size='small'
          type='date'
          label='Expiration date (BUD)'
          required={!entry.budLocked}
          value={entry.manualBud}
          onChange={(e) => entry.setManualBud(e.target.value)}
          disabled={fieldsDisabled || entry.budLocked}
          InputLabelProps={{ shrink: true }}
          inputProps={{
            'data-testid': 'manual-lot-bud',
            ...(entry.budLocked ? {} : { min: getDispensableBudMinIso() }),
          }}
          error={!!entry.budTooEarly}
          helperText={budHelperText}
          sx={{ flex: '1 1 220px' }}
        />
      </Box>
      {statusAlert}
    </Box>
  );
};

ManualLotPanel.propTypes = {
  typedLot: PropTypes.string.isRequired,
  entry: PropTypes.shape({
    manualBud: PropTypes.string.isRequired,
    setManualBud: PropTypes.func.isRequired,
    confirmLotNumber: PropTypes.string.isRequired,
    setConfirmLotNumber: PropTypes.func.isRequired,
    hasNdcChoices: PropTypes.bool.isRequired,
    recordableLoading: PropTypes.bool.isRequired,
    existingLot: PropTypes.shape({ inventoryLotId: PropTypes.string }),
    budLocked: PropTypes.bool.isRequired,
    budTooEarly: PropTypes.string.isRequired,
    needsLotConfirmation: PropTypes.bool.isRequired,
    lookupPending: PropTypes.bool.isRequired,
    error: PropTypes.string,
  }).isRequired,
};

export default ManualLotPanel;
